import { useMemo, useState, useEffect, useRef } from 'react';
import { Box, VStack } from '@chakra-ui/react';
import { toaster } from '@/lib/toaster';
import { useAutoScroll } from '@/hooks/useAutoScroll';
import { speakText, isTextToSpeechAvailable } from '@/utils/textToSpeech';
import { getRandomPrompts } from '@/utils/chatHelpers';
import {
  GENERAL_HELP_PROMPTS,
  RECIPE_HELP_PROMPTS,
  COLLECTION_HELP_PROMPTS,
  GENERAL_COOKING_POOL,
  RECIPE_COOKING_POOL,
  COLLECTION_COOKING_POOL,
  type PromptGroup,
} from '@/constants/chatPrompts';
import type { ChatMessage as ChatMessageData } from '@/types/chatContext';
import type { Recipe } from '@/types/api-models';
import type { RecipeCollection } from '@/types/api';
import type { PendingAction } from '@/types/chat';
import ChatMessage from './ChatMessage';
import ChatWelcomeCard from './ChatWelcomeCard';
import ChatHelpScreen from './ChatHelpScreen';
import ChatLoadingIndicator from './ChatLoadingIndicator';
import ChatInput from './ChatInput';
import ChatActionCard from './ChatActionCard';

interface ChatConversationProps {
  messages: ChatMessageData[];
  isLoading: boolean;
  onSend: (message: string) => void;
  recipeId?: string;
  currentRecipe?: Recipe;
  currentCollection?: RecipeCollection;
  inputPlaceholder?: string;
  helpMode?: boolean;
  pendingAction?: PendingAction | null;
  onConfirmAction?: () => void;
  onCancelAction?: () => void;
  isConfirmingAction?: boolean;
}

export default function ChatConversation({
  messages,
  isLoading,
  onSend,
  recipeId,
  currentRecipe,
  currentCollection,
  inputPlaceholder = 'Message',
  helpMode = false,
  pendingAction,
  onConfirmAction,
  onCancelAction,
  isConfirmingAction = false,
}: ChatConversationProps) {
  const messagesEndRef = useAutoScroll<HTMLDivElement>([messages.length]);
  const [focusTrigger, setFocusTrigger] = useState(0);
  const prevPendingAction = useRef(pendingAction);

  useEffect(() => {
    if (prevPendingAction.current !== null && pendingAction === null) {
      setFocusTrigger((n) => n + 1);
    }
    prevPendingAction.current = pendingAction;
  }, [pendingAction]);

  const suggestedGroups = useMemo<PromptGroup[]>(() => {
    if (recipeId) {
      return [
        { label: 'Help', prompts: RECIPE_HELP_PROMPTS },
        { label: 'This recipe', prompts: getRandomPrompts(RECIPE_COOKING_POOL, 3) },
      ];
    }
    if (currentCollection) {
      return [
        { label: 'Help', prompts: COLLECTION_HELP_PROMPTS },
        { label: 'This collection', prompts: getRandomPrompts(COLLECTION_COOKING_POOL, 3) },
      ];
    }
    return [
      { label: 'Help', prompts: GENERAL_HELP_PROMPTS },
      { label: 'Recipes', prompts: getRandomPrompts(GENERAL_COOKING_POOL, 3) },
    ];
  }, [recipeId, currentCollection]);

  const handleReadAloud = (text: string) => {
    if (!isTextToSpeechAvailable()) {
      toaster.create({
        title: 'Text-to-speech not available',
        description: "Your browser doesn't support text-to-speech",
        type: 'error',
      });
      return;
    }
    try {
      speakText(text);
    } catch {
      toaster.create({
        title: 'Text-to-speech error',
        description: 'Failed to read message aloud',
        type: 'error',
      });
    }
  };

  return (
    <Box display="flex" flexDirection="column" flex="1" minH="0" h="100%">

      <Box flex="1" overflowY="auto" overflowX="hidden" p={4} minH="0" bg="bg.canvas">
        {messages.length === 0 && !isLoading ? (
          helpMode ? (
            <ChatHelpScreen onSelectPrompt={onSend} />
          ) : (
            <ChatWelcomeCard
              currentRecipe={currentRecipe}
              currentCollection={currentCollection}
              suggestedGroups={suggestedGroups}
              onSelectPrompt={onSend}
            />
          )
        ) : (
          <VStack gap={4} align="stretch">
            {messages.map((msg, idx) => (
              <ChatMessage
                key={idx}
                role={msg.role}
                content={msg.content}
                isFirstOfRole={idx === 0 || messages[idx - 1].role !== msg.role}
                onReadAloud={handleReadAloud}
              />
            ))}
            {isLoading && <ChatLoadingIndicator />}
            <div ref={messagesEndRef} />
          </VStack>
        )}
      </Box>

      {pendingAction && onConfirmAction && onCancelAction && (
        <ChatActionCard
          action={pendingAction}
          onConfirm={onConfirmAction}
          onCancel={onCancelAction}
          isConfirming={isConfirmingAction}
        />
      )}

      <ChatInput
        onSend={onSend}
        isLoading={isLoading}
        placeholder={inputPlaceholder}
        messageCount={messages.length}
        focusTrigger={focusTrigger}
      />
    </Box>
  );
}
